import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const PHASE_CONFIG = {
  downloading: {
    label: "Downloading firmware...",
    sublabel: "Fetching latest version",
    progress: 30,
  },
  installing: {
    label: "Installing firmware...",
    sublabel: "Writing to flash memory",
    progress: 65,
  },
  rebooting: {
    label: "Rebooting device...",
    sublabel: "Applying update",
    progress: 85,
  },
  complete: {
    label: "Update complete!",
    sublabel: null,
    progress: 100,
  },
};

const FirmwareCard = ({ device, onUpdate, updatePhase }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isUpdating = updatePhase && updatePhase !== "complete";
  const phaseInfo = updatePhase ? PHASE_CONFIG[updatePhase] : null;

  return (
    <>
      <Card
        sx={{
          height: "100%",
          ...(isUpdating && {
            bgcolor: "#3b82f60a",
            border: "1px solid #3b82f630",
          }),
          ...(updatePhase === "complete" && {
            bgcolor: "#22c55e0a",
            border: "1px solid #22c55e30",
          }),
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            {updatePhase === "complete" ? (
              <CheckCircleIcon sx={{ color: "#22c55e" }} />
            ) : (
              <SystemUpdateAltIcon
                color={isUpdating ? "info" : "primary"}
                sx={{
                  ...(isUpdating && {
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.4 },
                    },
                    animation: "pulse 1.5s ease-in-out infinite",
                  }),
                }}
              />
            )}
            <Typography variant="subtitle1" fontWeight="bold">
              Firmware
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: "action.hover",
              mb: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Current Version
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {device?.firmware_version || "Unknown"}
            </Typography>
          </Box>

          {device?.runtime && (
            <Box display="flex" alignItems="center" gap={0.5} mb={2}>
              <InfoOutlinedIcon
                sx={{ fontSize: 16, color: "text.secondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                Runtime: {device.runtime}
              </Typography>
            </Box>
          )}

          {/* Update progress */}
          {phaseInfo && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={phaseInfo.progress}
                color={updatePhase === "complete" ? "success" : "info"}
                sx={{ height: 6, borderRadius: 3, mb: 1 }}
              />
              <Typography
                variant="body2"
                fontWeight="medium"
                textAlign="center"
                sx={{
                  color:
                    updatePhase === "complete" ? "#22c55e" : "text.primary",
                }}
              >
                {phaseInfo.label}
              </Typography>
              {phaseInfo.sublabel && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  display="block"
                >
                  {phaseInfo.sublabel}
                </Typography>
              )}
              {isUpdating && device?.device_status && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  textAlign="center"
                  display="block"
                  sx={{ mt: 0.5, fontStyle: "italic" }}
                >
                  {device.device_status}
                </Typography>
              )}
            </Box>
          )}

          <Button
            variant="outlined"
            fullWidth
            startIcon={<SystemUpdateAltIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={device?.status !== "ONLINE" || isUpdating}
          >
            {isUpdating ? "Updating..." : "Update Firmware"}
          </Button>

          {!isUpdating && device?.status !== "ONLINE" && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Device must be online to update firmware
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Update Firmware</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you'd like to update the device's firmware? The device
            will download and install the latest version from the main branch.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              onUpdate();
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FirmwareCard;
